import React, { FC, useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { ProductModel } from '../../../../../shared/orders';
import * as OrderSummary from '../../../../../shared/orders/model';
import { yearDiff } from '../../../../../shared/util/time';
import { ElementSelector } from '../../../../components';
import { MaterialIconText } from '../../../../components/MaterialIconText';
import { JSONForm } from '../../../../components/json-form/JSONForm';
import { useTranslation } from '../../../../translations';
import {
  Fetcher,
  handleUnknownOptionError,
  isLogicError,
  useConvention,
  useFetcher,
  useToggle,
} from '../../../../utils';
import { LogicError } from '../../../../utils/errorHandling';
import { LocalSearchProvider, ProductInfoBadges } from '../../products/utils';

interface OrderAddProductModalProps {
  readonly user: { bornAt?: Date | null };
  readonly order: OrderSummary.Order;
  readonly onSuccess: () => void;
  readonly onCancel: () => void;
}

export const OrderAddProductModal: FC<OrderAddProductModalProps> = ({
  user,
  order,
  onCancel,
  onSuccess,
}) => {
  const { ts } = useTranslation();
  const [productId, setProductId] = useState<number | undefined>(undefined);
  const productList = useFetcher(async () => await api.getProducts(), []);
  const start = useConvention().startAt;
  const [confirmAge, toggleConfirmAge] = useToggle(false);
  const [confirmOutOfStock, toggleConfirmOutOfStock] = useToggle(false);
  const searchProvider = useMemo(
    () => new LocalSearchProvider(() => productList.data!),
    [productList],
  );

  const selected = productList.data?.find((d) => d.id === productId);
  const outOfStock =
    selected && selected.stockMaximumQuantity !== null && selected.stockAvailable === 0;

  // TODO: Fix scenario where age is not provided
  const userAge = user.bornAt ? yearDiff(user.bornAt, start) : 999;
  const notInAgeRange =
    selected &&
    ((selected.minAge && userAge < selected.minAge) ||
      (selected.maxAge && userAge > selected.maxAge));

  const canSubmit =
    !!productId && (!notInAgeRange || confirmAge) && (!outOfStock || confirmOutOfStock);

  const onFailure = useCallback(
    (error: Error) => {
      if (isLogicError(error, LogicError.UnknownProductOption)) {
        handleUnknownOptionError(error.apiResponse.errors.logic, selected?.options);

        return true;
      }

      return false;
    },
    [selected],
  );

  return (
    <Modal className="modal-large" id="addProductModal" isOpen>
      <JSONForm
        method="post"
        onFailure={onFailure}
        onSuccess={onSuccess}
        path={`/api/orders/${order.id}/product`}
      >
        <ModalHeader>{ts('order_add_product_modal.add_product_to_order', order.id)}</ModalHeader>
        <ModalBody>
          <p>{ts('order_add_product_modal.select_a_product_force_added')}</p>
          <Input name="productId" type="hidden" value={productId?.toString() ?? ''} />
          {productList.data ? (
            <FormGroup>
              <Label for="product">{ts('common.product_search')}</Label>
              <ElementSelector<ProductModel>
                id="addProductSelector"
                maxItems={1}
                searchProvider={searchProvider}
                singleSelectionIdChanged={setProductId}
              />
            </FormGroup>
          ) : (
            <Fetcher result={productList} />
          )}
          {selected && <ProductInfoBadges product={selected} />}
          {notInAgeRange && (
            <Alert
              color="danger"
              id="ageWarning"
              onClick={toggleConfirmAge}
              style={{ cursor: 'pointer' }}
            >
              <MaterialIconText
                name={confirmAge ? 'check_box' : 'check_box_outline_blank'}
                style={{ cursor: 'pointer' }}
              >
                {ts('order_add_product_modal.confirm_that_the_user_does', userAge)}
              </MaterialIconText>
            </Alert>
          )}
          {outOfStock && (
            <Alert
              color="danger"
              id="outOfStockWarning"
              onClick={toggleConfirmOutOfStock}
              style={{ cursor: 'pointer' }}
            >
              <MaterialIconText
                name={confirmOutOfStock ? 'check_box' : 'check_box_outline_blank'}
                style={{ cursor: 'pointer' }}
              >
                {ts('order_add_product_modal.confirm_that_you_are_adding')}
              </MaterialIconText>
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={onCancel}>
            {ts('common.cancel')}
          </Button>{' '}
          <Button color="primary" disabled={!canSubmit} id="addOrderProduct" type="submit">
            {ts('common.add')}
          </Button>
        </ModalFooter>
      </JSONForm>
    </Modal>
  );
};
